import { io, type Socket } from "socket.io-client";

const apiBaseUrl =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api";
const socketUrl = apiBaseUrl.replace("/api", "");

interface RequestOptions extends RequestInit {
  body?: any;
}

interface ApiResponse<T = any> {
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
  status: number;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private workspaceId: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadToken();
  }

  private loadToken() {
    if (typeof window !== "undefined") {
      this.token = localStorage.getItem("auth_token");
      this.workspaceId = localStorage.getItem("workspace_id");
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    // Re-read from localStorage each time — covers the case where
    // setWorkspace() was called after the singleton was constructed.
    const workspaceId =
      this.workspaceId ||
      (typeof window !== "undefined"
        ? localStorage.getItem("workspace_id")
        : null);
    if (workspaceId) {
      headers["x-workspace-id"] = workspaceId;
    }

    return headers;
  }

  setToken(token: string | null) {
    this.token = token;
    if (token && typeof window !== "undefined") {
      localStorage.setItem("auth_token", token);
    } else if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token");
    }
  }

  setWorkspace(id: string | null) {
    this.workspaceId = id;
    if (id && typeof window !== "undefined") {
      localStorage.setItem("workspace_id", id);
    } else if (typeof window !== "undefined") {
      localStorage.removeItem("workspace_id");
    }
  }

  getWorkspace(): string | null {
    return this.workspaceId;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders();

    const fetchOptions: RequestInit = {
      ...options,
      headers: { ...headers, ...(options.headers || {}) },
    };

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, fetchOptions);
      const contentType = response.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      let data: any = null;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        if (response.status === 401 && typeof window !== "undefined") {
          this.setToken(null);
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return {
          data: undefined,
          error: {
            message:
              data?.message ||
              data?.error ||
              (typeof data === "string" ? data : null) ||
              response.statusText,
            code: String(response.status),
          },
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      return {
        data: undefined,
        error: {
          message,
          code: "NETWORK_ERROR",
        },
        status: 0,
      };
    }
  }

  get<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: "GET" });
  }

  post<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, { method: "POST", body });
  }

  put<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, { method: "PUT", body });
  }

  patch<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, { method: "PATCH", body });
  }

  delete<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  async postForm<T = any>(
    endpoint: string,
    formData: FormData,
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {};
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    if (this.workspaceId) headers["x-workspace-id"] = this.workspaceId;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });
      const data = await response.json().catch(() => null);
      if (!response.ok)
        return {
          error: {
            message: data?.error || response.statusText,
            code: String(response.status),
          },
          status: response.status,
        };
      return { data, status: response.status };
    } catch (err) {
      return {
        error: {
          message: err instanceof Error ? err.message : "Network error",
          code: "NETWORK_ERROR",
        },
        status: 0,
      };
    }
  }
}

export const api = new ApiClient(apiBaseUrl);

let _userCache: any = null;
let _userCacheAt = 0;
let _userInflight: Promise<any> | null = null;
const USER_CACHE_MS = 5 * 60 * 1000;

export const getCurrentUser = async () => {
  const token = localStorage.getItem("auth_token");
  if (!token) return null;

  if (_userCache && Date.now() - _userCacheAt < USER_CACHE_MS) {
    return _userCache;
  }

  // Dedup concurrent callers (e.g. boot init + first navigation guard)
  if (_userInflight) return _userInflight;

  _userInflight = (async () => {
    const response = await api.get("/auth/user");
    if (response.error) {
      // Invalid/expired token: drop it so we stop re-issuing 401 requests
      if (response.status === 401) api.setToken(null);
      return null;
    }
    _userCache = response.data?.user || null;
    _userCacheAt = Date.now();
    // Restore workspace if missing from localStorage (e.g. after page refresh
    // on a session that predates workspace_id being persisted).
    if (!api.getWorkspace() && response.data?.workspaces?.length) {
      api.setWorkspace(response.data.workspaces[0].id);
    }
    return _userCache;
  })();

  try {
    return await _userInflight;
  } finally {
    _userInflight = null;
  }
};

export const clearUserCache = () => {
  _userCache = null;
  _userCacheAt = 0;
  _userInflight = null;
};

export const signOut = async () => {
  api.setToken(null);
};

export const signInWithEmail = async (email: string, password: string) => {
  const response = await api.post("/auth/login", { email, password });
  if (response.error) throw new Error(response.error.message);
  if (response.data?.token) api.setToken(response.data.token);
  if (response.data?.workspaces?.length) api.setWorkspace(response.data.workspaces[0].id);
  return response.data;
};

export const signUpWithEmail = async (
  email: string,
  password: string,
  metadata?: any,
) => {
  const response = await api.post("/auth/register", {
    email,
    password,
    first_name: metadata?.first_name,
    last_name: metadata?.last_name,
  });
  if (response.error) throw new Error(response.error.message);
  if (response.data?.token) api.setToken(response.data.token);
  if (response.data?.workspace_id) api.setWorkspace(response.data.workspace_id);
  return response.data;
};

let _socket: Socket | null = null;

export const getSocket = (): Socket => {
  if (!_socket) {
    _socket = io(socketUrl, {
      auth: { token: localStorage.getItem("auth_token") },
      autoConnect: true,
      reconnection: true,
    });
  }
  return _socket;
};

export const joinWorkspace = (workspaceId: string) => {
  getSocket().emit("join:workspace", workspaceId);
};

export const subscribeToChanges = (
  event: string,
  callback: (payload: any) => void,
) => {
  const socket = getSocket();
  socket.on(event, callback);
  return {
    unsubscribe: () => socket.off(event, callback),
  };
};
